import { TicketsByType } from '@/modules/Ticket/types/response'
import { TicketTypeGroupOptions } from '@/modules/Ticket/types/utils'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface TicketsByTypeChartProps {
    ticketsByType: TicketsByType[]
}

const GROUP_COLORS: Record<string, string> = {
    support_request: '#3B82F6',
    error_service: '#EF4444',
    meeting: '#10B981',
    service: '#F59E0B',
    service_request: '#8B5CF6',
    change: '#EC4899',
    default: '#6B7280',
}

const TicketsByTypeChart: React.FC<TicketsByTypeChartProps> = ({
    ticketsByType = [],
}) => {
    const { t } = useTranslation('ticket')
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null)

    const total = ticketsByType.reduce((sum, item) => sum + item.group_total, 0)

    // Create a map of group value to label from TicketTypeGroupOptions
    const groupLabels = useMemo(() => {
        const options = TicketTypeGroupOptions(t)
        return options.reduce(
            (acc, opt) => {
                acc[opt.value] = opt.label
                return acc
            },
            {} as Record<string, string>
        )
    }, [t])

    // Sort by group_total descending
    const sortedData = [...ticketsByType].sort(
        (a, b) => b.group_total - a.group_total
    )

    const toggleGroup = (group: string) => {
        setExpandedGroup(expandedGroup === group ? null : group)
    }

    console.log(ticketsByType)
    console.log(sortedData)
    return (
        <div className="bg-white rounded-[8px] p-4 lg:col-span-4 md:col-span-1 col-span-1 half-col-print">
            <div className="flex items-center justify-between mb-3">
                <Typogaphy size="small" className="text-primary font-semibold">
                    {t('ticket-by-type')}
                </Typogaphy>
                {total > 0 && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {t('ticket-total')}: {total}
                    </span>
                )}
            </div>

            <div className="h-[280px] overflow-y-auto">
                {ticketsByType.length === 0 || total === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <Typogaphy className="text-gray-500 text-center text-sm">
                            {t('no-data')}
                        </Typogaphy>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {sortedData.map((group) => {
                            const isExpanded =
                                expandedGroup === group.ticket_type_group
                            const groupColor =
                                GROUP_COLORS[group.ticket_type_group] ||
                                GROUP_COLORS.default
                            const groupPercentage =
                                total > 0
                                    ? (group.group_total / total) * 100
                                    : 0

                            return (
                                <div key={group.ticket_type_group}>
                                    {/* Group Row */}
                                    <div
                                        className="cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                        onClick={() =>
                                            toggleGroup(group.ticket_type_group)
                                        }
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-2">
                                                {group.ticket_types.length >
                                                0 ? (
                                                    isExpanded ? (
                                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                                    ) : (
                                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                                    )
                                                ) : (
                                                    <div className="w-4" />
                                                )}
                                                <div
                                                    className="w-2 h-2 rounded-sm"
                                                    style={{
                                                        backgroundColor:
                                                            groupColor,
                                                    }}
                                                />
                                                <span className="text-sm font-medium text-gray-700">
                                                    {groupLabels[
                                                        group.ticket_type_group
                                                    ] ||
                                                        group.ticket_type_group}
                                                </span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-800">
                                                {group.group_total}
                                            </span>
                                        </div>
                                        {/* Progress Bar */}
                                        <div className="ml-6 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                                            <div
                                                className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                                                style={{
                                                    width: `${groupPercentage}%`,
                                                    backgroundColor: groupColor,
                                                    minWidth:
                                                        groupPercentage > 0
                                                            ? '40px'
                                                            : '0',
                                                }}
                                            >
                                                <span className="text-xs font-medium text-white">
                                                    {groupPercentage.toFixed(1)}
                                                    %
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Children */}
                                    {isExpanded &&
                                        group.ticket_types.length > 0 && (
                                            <div className="ml-8 mt-1 space-y-1 border-l-2 border-gray-200 pl-3 py-1">
                                                {group.ticket_types
                                                    .sort(
                                                        (a, b) =>
                                                            b.total_tickets -
                                                            a.total_tickets
                                                    )
                                                    .map((type) => {
                                                        const typePercentage =
                                                            total > 0
                                                                ? (type.total_tickets /
                                                                      total) *
                                                                  100
                                                                : 0
                                                        return (
                                                            <div
                                                                key={
                                                                    type.ticket_type_id
                                                                }
                                                                className="py-1"
                                                            >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span
                                                                        className="text-xs text-gray-600 truncate w-full"
                                                                        title={
                                                                            type.ticket_type_title
                                                                        }
                                                                    >
                                                                        {
                                                                            type.ticket_type_title
                                                                        }
                                                                    </span>
                                                                    <span className="text-xs font-medium text-gray-700">
                                                                        {
                                                                            type.total_tickets
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <div className="h-3 bg-gray-50 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full rounded-full transition-all duration-300 flex items-center justify-end pr-1"
                                                                        style={{
                                                                            width: `${typePercentage}%`,
                                                                            backgroundColor:
                                                                                groupColor,
                                                                            opacity: 0.6,
                                                                            minWidth:
                                                                                typePercentage >
                                                                                0
                                                                                    ? '32px'
                                                                                    : '0',
                                                                        }}
                                                                    >
                                                                        <span className="text-[10px] font-medium text-white">
                                                                            {typePercentage.toFixed(
                                                                                1
                                                                            )}
                                                                            %
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                            </div>
                                        )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

export default TicketsByTypeChart
